import dynamic from 'next/dynamic'
import { FunctionComponent, memo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useUpdateUserMetadataMutation } from '@/api/user-api'
import { closeDrawer } from '@/components/tour/utils'
import checkTourRead from '@/components/tour/utils/check-tour-read'
import routes from '@/hooks/use-authorization/routes'
import { useSelector } from '@/hooks/use-selector'
import useUserMetadata from '@/hooks/use-user-metadata'
import { reduxAPI } from '@/store/redux-api'
import {
  TOUR_KEY_EVENT_NAME_MAPPING,
  TourKey,
  setSelectedTour,
  setTourModalVisible,
  setTourStepAction,
} from '@/store/tour'
import { ampli } from '@/utils/ampli'
import { exhaustiveCheck } from '@/utils/exhaustive-check'
import logger from '@/utils/logger'

const AbortTour = dynamic(() => import('@/components/tour/abort-tour'))
const DirectoryTour = dynamic(() => import('@/components/tour/directory-tour'))
const FinalTour = dynamic(() => import('@/components/tour/final-tour'))
const KolCollectionsTour = dynamic(
  () => import('@/components/tour/kol-collections-tour'),
)
const MarketingTrendsTour = dynamic(
  () => import('@/components/tour/marketing-trend-tour'),
)
const PredictCompetitorTour = dynamic(
  () => import('@/components/tour/predict-competitor-tour'),
)
const PredictiveReportTour = dynamic(
  () => import('@/components/tour/predictive-report-tour'),
)
const ReportPerformanceTour = dynamic(
  () => import('@/components/tour/report-performance-tour'),
)
const SearchKolTour = dynamic(() => import('@/components/tour/search-kol-tour'))
const SocialPerformanceTour = dynamic(
  () => import('@/components/tour/social-performance-tour'),
)
const WorldKolTour = dynamic(() => import('@/components/tour/world-kol-tour'))

const RadarTour: FunctionComponent = () => {
  const dispatch = useDispatch()
  const modal = reduxAPI.state.modal.hooks.useVisible('onboardingModal')
  const { tourModalVisible, selectedTour } = useSelector((state) => state.tour)
  const [lastTour, setLastTour] = useState<TourKey | null>(null)
  const { userMetadata } = useUserMetadata()
  const [updateUserMetadata] = useUpdateUserMetadataMutation()

  const resetState = (reOpenOnboardingModal = true): void => {
    setLastTour(null)
    dispatch(setTourModalVisible(false))
    dispatch(setTourStepAction(undefined))
    dispatch(setSelectedTour('search-kol'))

    if (reOpenOnboardingModal) {
      modal.openModal()
    }
  }

  const completeOnboarding = async (): Promise<void> => {
    if (userMetadata && !userMetadata.completed_onboarding) {
      await updateUserMetadata({
        ...userMetadata,
        completed_onboarding: true,
      })
    }
  }

  const handleAbortTourFinish = (): void => {
    completeOnboarding()
    resetState(false)
  }

  const handleFinalTourFinish = (): void => {
    completeOnboarding()

    if (lastTour) {
      ampli.completedOnboarding({
        onboardingItem: TOUR_KEY_EVENT_NAME_MAPPING[lastTour],
      })
    }

    resetState()
  }

  const handleWorldKolTourFinish = (): void => {
    completeOnboarding()
    resetState(false)
  }

  const handleDirectoryTourFinish = (): void => {
    completeOnboarding()
    ampli.completedOnboarding({
      onboardingItem: TOUR_KEY_EVENT_NAME_MAPPING['directory'],
    })

    closeDrawer()
    resetState(false)
  }

  const handleKolCollectionsTourFinish = (): void => {
    completeOnboarding()
    ampli.completedOnboarding({
      onboardingItem: TOUR_KEY_EVENT_NAME_MAPPING['kol-collections'],
    })

    closeDrawer()
    resetState(false)
    routes.collectionsKol.goto({})
  }

  const handleFinish = (): void => {
    completeOnboarding()
    setLastTour(selectedTour)
    dispatch(setSelectedTour('final'))
  }

  const handleSkipOnboarding = (): void => {
    completeOnboarding()
    resetState(false)
  }

  const handleDirectoryTourClose = (): void => {
    completeOnboarding()
    resetState(false)
    ampli.closeOnboardingTutorial({
      onboardingItem: TOUR_KEY_EVENT_NAME_MAPPING['directory'],
    })
  }

  const handleKolCollectionsTourClose = (): void => {
    completeOnboarding()
    resetState(false)
    ampli.closeOnboardingTutorial({
      onboardingItem: TOUR_KEY_EVENT_NAME_MAPPING['kol-collections'],
    })
  }

  const createCloseHandler =
    (tourKey: TourKey) =>
    (isLastStep: boolean): void => {
      completeOnboarding()
      dispatch(setSelectedTour('abort'))

      if (isLastStep) {
        return
      }

      ampli.closeOnboardingTutorial({
        onboardingItem: TOUR_KEY_EVENT_NAME_MAPPING[tourKey],
      })
    }

  const createErrorHandler =
    (tourKey: TourKey) =>
    (error: unknown): void => {
      resetState(!['directory', 'kol-collections'].includes(tourKey))
      logger.error(error)
    }

  const createOpenHandler = (tourKey: TourKey) => (): void => {
    const onboardingItem = TOUR_KEY_EVENT_NAME_MAPPING[tourKey]
    if (!onboardingItem) {
      return
    }

    const isTourRead = checkTourRead(tourKey, userMetadata)

    if (isTourRead) {
      ampli.replayOnboarding({
        onboardingItem,
      })
    } else {
      ampli.startOnboardingTutorial({
        onboardingItem,
      })
    }
  }

  switch (selectedTour) {
    case 'abort':
      return (
        <AbortTour
          open={tourModalVisible}
          onClose={handleSkipOnboarding}
          onError={createErrorHandler('abort')}
          onFinish={handleAbortTourFinish}
        />
      )
    case 'directory':
      return (
        <DirectoryTour
          open={tourModalVisible}
          onClose={handleDirectoryTourClose}
          onError={createErrorHandler('directory')}
          onFinish={handleDirectoryTourFinish}
          onOpen={createOpenHandler('directory')}
        />
      )
    case 'final':
      return (
        <FinalTour
          open={tourModalVisible}
          onClose={handleSkipOnboarding}
          onError={createErrorHandler('final')}
          onFinish={handleFinalTourFinish}
        />
      )
    case 'kol-collections':
      return (
        <KolCollectionsTour
          open={tourModalVisible}
          onClose={handleKolCollectionsTourClose}
          onError={createErrorHandler('kol-collections')}
          onFinish={handleKolCollectionsTourFinish}
          onOpen={createOpenHandler('kol-collections')}
        />
      )
    case 'marketing-trend':
      return (
        <MarketingTrendsTour
          open={tourModalVisible}
          onClose={createCloseHandler('marketing-trend')}
          onError={createErrorHandler('marketing-trend')}
          onFinish={handleFinish}
          onOpen={createOpenHandler('marketing-trend')}
        />
      )
    case 'predict-competitor':
      return (
        <PredictCompetitorTour
          open={tourModalVisible}
          onClose={createCloseHandler('predict-competitor')}
          onError={createErrorHandler('predict-competitor')}
          onFinish={handleFinish}
          onOpen={createOpenHandler('predict-competitor')}
        />
      )
    case 'predictive-report':
      return (
        <PredictiveReportTour
          open={tourModalVisible}
          onClose={createCloseHandler('predictive-report')}
          onError={createErrorHandler('predictive-report')}
          onFinish={handleFinish}
          onOpen={createOpenHandler('predictive-report')}
        />
      )
    case 'report-performance':
      return (
        <ReportPerformanceTour
          open={tourModalVisible}
          onClose={createCloseHandler('report-performance')}
          onError={createErrorHandler('report-performance')}
          onFinish={handleFinish}
          onOpen={createOpenHandler('report-performance')}
        />
      )
    case 'search-kol':
      return (
        <SearchKolTour
          open={tourModalVisible}
          onClose={createCloseHandler('search-kol')}
          onError={createErrorHandler('search-kol')}
          onFinish={handleFinish}
          onOpen={createOpenHandler('search-kol')}
        />
      )
    case 'social-performance':
      return (
        <SocialPerformanceTour
          open={tourModalVisible}
          onClose={createCloseHandler('social-performance')}
          onError={createErrorHandler('social-performance')}
          onFinish={handleFinish}
          onOpen={createOpenHandler('social-performance')}
        />
      )
    case 'world-kol':
      return (
        <WorldKolTour
          open={tourModalVisible}
          onClose={handleSkipOnboarding}
          onError={createErrorHandler('world-kol')}
          onFinish={handleWorldKolTourFinish}
          onOpen={createOpenHandler('world-kol')}
        />
      )
    default:
      return exhaustiveCheck(selectedTour)
  }
}

export default memo(RadarTour)
